import { forwardRef, useCallback, useEffect, useState } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import { addMonths, subDays, subMonths, startOfDay } from "date-fns";
import { CalendarHeader } from "./CalendarHeader";
import { CalendarGrid } from "./CalendarGrid";
import { CalendarChips } from "./CalendarChips";
import type { CalendarProps } from "./types";

export const Calendar = forwardRef<any, CalendarProps>(
  (
    {
      locale = "enUS",
      onChange,
      startDate,
      endDate,
      selectedDate,
      selectsRange = false,
      contextMenuMaxHeight,
      topContent,
      bottomContent,
      chips,
      activeChip,
      onChipSelect,
      testID,
      minDate,
      maxDate,
      firstDayOfWeek = 0,
      month,
      initialMonth,
      themeMode,
      themeProductContext,
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });

    const [currentMonth, setCurrentMonth] = useState<Date>(
      (month || initialMonth || selectedDate || startDate || new Date()) as Date
    );
    const [selectedStartDate, setSelectedStartDate] = useState<
      Date | null | undefined
    >(startDate);
    const [selectedEndDate, setSelectedEndDate] = useState<
      Date | null | undefined
    >(endDate);
    const [locSelectedDate, setLocSelectedDate] = useState<
      Date | null | undefined
    >(selectedDate);
    const [selectingRange, setSelectingRange] = useState<Date | null>(null);

    useEffect(() => {
      setSelectedStartDate(startDate);
      setSelectedEndDate(endDate);
      setLocSelectedDate(selectedDate);
      if (month !== undefined) {
        setCurrentMonth(month);
      }
    }, [startDate, endDate, selectedDate, month]);

    const handleChange = useCallback(
      (date: Date) => {
        const newDate = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate()
        );

        if (!selectsRange) {
          setLocSelectedDate(newDate);
          onChange?.(newDate);
        } else {
          if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
            setSelectedStartDate(newDate);
            setSelectedEndDate(null);
            setSelectingRange(newDate);
            onChange?.([newDate, null]);
          } else {
            const start =
              selectedStartDate < newDate ? selectedStartDate : newDate;
            const end =
              selectedStartDate < newDate ? newDate : selectedStartDate;
            setSelectedStartDate(start);
            setSelectedEndDate(end);
            setSelectingRange(null);
            onChange?.([start, end]);
          }
        }
      },
      [onChange, selectsRange, selectedStartDate, selectedEndDate]
    );

    const decreaseMonth = useCallback(
      () => setCurrentMonth((prev) => subMonths(prev, 1)),
      []
    );
    const increaseMonth = useCallback(
      () => setCurrentMonth((prev) => addMonths(prev, 1)),
      []
    );
    const changeMonth = useCallback(
      (m: number) =>
        setCurrentMonth((prev) => new Date(prev.getFullYear(), m, 1)),
      []
    );
    const changeYear = useCallback(
      (y: number) => setCurrentMonth((prev) => new Date(y, prev.getMonth(), 1)),
      []
    );

    const handleChipSelect = useCallback(
      (value: string | null) => {
        onChipSelect?.(value);

        const chip = chips?.find((c) => c.value === value);
        if (!chip || chip.days === undefined) return;

        const today = startOfDay(new Date());

        if (chip.days === 0) {
          if (selectsRange) {
            setSelectedStartDate(today);
            setSelectedEndDate(today);
            setSelectingRange(null);
            onChange?.([today, today]);
          } else {
            setLocSelectedDate(today);
            onChange?.(today);
          }
          setCurrentMonth(today);
          return;
        }

        const start = subDays(today, chip.days);
        if (selectsRange) {
          setSelectedStartDate(start);
          setSelectedEndDate(today);
          setSelectingRange(null);
          onChange?.([start, today]);
        } else {
          setLocSelectedDate(start);
          onChange?.(start);
        }
        setCurrentMonth(start);
      },
      [chips, selectsRange, onChange, onChipSelect]
    );

    return (
      <Box
        ref={ref}
        testID={testID}
        backgroundColor={theme.colors.layer.float}
        borderRadius={theme.shape.contextMenu.md.borderRadius}
        padding={16}
        overflow="hidden"
        style={{
          boxShadow:
            "0px 2px 3px 0px rgba(7, 7, 8, 0.2), 0px 6px 10px 4px rgba(7, 7, 8, 0.1)",
          width: 312,
        }}
      >
        {topContent?.({ close: () => {} })}

        {chips && chips.length > 0 && (
          <CalendarChips
            chips={chips}
            activeChip={activeChip}
            onChipSelect={handleChipSelect}
            themeMode={themeMode}
            themeProductContext={themeProductContext}
          />
        )}

        <CalendarHeader
          monthDate={currentMonth}
          decreaseMonth={decreaseMonth}
          increaseMonth={increaseMonth}
          changeYear={changeYear}
          changeMonth={changeMonth}
          locale={locale}
          minDate={minDate}
          maxDate={maxDate}
          contextMenuMaxHeight={contextMenuMaxHeight}
          themeMode={themeMode}
          themeProductContext={themeProductContext}
        />

        <CalendarGrid
          currentMonth={currentMonth}
          locale={locale}
          firstDayOfWeek={firstDayOfWeek}
          selectsRange={selectsRange}
          minDate={minDate}
          maxDate={maxDate}
          startDate={selectedStartDate}
          endDate={selectedEndDate}
          selectedDate={locSelectedDate}
          selectingRange={selectingRange}
          onDayPress={handleChange}
          themeMode={themeMode}
          themeProductContext={themeProductContext}
        />

        {bottomContent?.({ close: () => {} })}
      </Box>
    );
  }
);

Calendar.displayName = "Calendar";
